/// "Talk to the developer" board — Firestore data layer + helpers.
///
/// Collections (all new, web*-namespaced):
///   webDevPosts/{postId}
///     votes/{voterUid}                       { value: 1 | -1, votedAt }
///     comments/{commentId}
///       votes/{voterUid}                     { value: 1 | -1, votedAt }
///
/// Reads are direct Firestore listeners (auth required, anonymous
/// OK). Post/comment/vote writes are direct too, gated by the
/// security rules. The aggregate count fields (upCount / downCount /
/// score / commentCount) and `lastActivityAt` are written ONLY by
/// the devBlog Cloud Function triggers (admin SDK) — clients have no
/// permission to set them, so this module never updates them.
///
/// Cascade delete (a post delete removing its comments + votes) is
/// the `onDevPostDeleted` trigger's job; `deletePost` here just
/// removes the post doc.

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit as limitTo,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentReference,
  type FirestoreError,
  type Timestamp,
} from 'firebase/firestore'
import type { User } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions, firestore } from '../firebase/app'

/// The developer's Firebase Auth email. Posts/comments authored by
/// this account *when verified* are flagged `authorIsDeveloper` and
/// highlighted. Google sign-in ⇒ email_verified is always true.
/// Mirrors the iOS `featurePolls` admin allowlist.
export const DEV_EMAIL = 'kennedy.craig@gmail.com'

export type VoteValue = 1 | -1
export type SortKey = 'newest' | 'top'

export interface DevPost {
  id: string
  /// Empty string on anonymous feedback posts (submitted via the
  /// `submitFeedbackPost` Cloud Function — the real submitter UID
  /// lives privately in `webFeedbackOwners/{postId}` so the public
  /// post carries no identifying data). Non-empty on normal Q&A
  /// posts authored by signed-in (incl. anonymous) Firebase users.
  authorUid: string
  authorName: string | null
  authorIsDeveloper: boolean
  title: string
  body: string
  /// Pending writes report `null` for a server timestamp until the
  /// write is acknowledged; callers read with `{ serverTimestamps:
  /// 'estimate' }` so a freshly created post still sorts correctly,
  /// but the field can still be momentarily `null`.
  createdAt: Timestamp | null
  editedAt: Timestamp | null
  /// When non-null, the post is archived: new comments and new votes
  /// are rejected by the security rules, and the page renders it in
  /// a separate section below the active posts. Toggle via
  /// `setPostArchived`; admin-only.
  archivedAt: Timestamp | null
  upCount: number
  downCount: number
  score: number
  commentCount: number
  /// 1-5 star rating on a feedback post; null on regular Q&A posts.
  /// Together with `feedbackLevelId` this identifies a post as
  /// feedback; the post card renders the star + level chip and
  /// suppresses the byline. Set server-side only.
  feedbackRating: number | null
  /// Level id (e.g. "PANC") the feedback was submitted from; null
  /// for general feedback (toolbar button outside a level) and for
  /// regular Q&A posts. Server-side only.
  feedbackLevelId: string | null
}

/// True when the post originated from the Feedback flow (rating-
/// based, anonymous, immutable from clients). The post card,
/// post-detail header, and the "Recent feedback" strip on the page
/// all branch off this.
export function isFeedbackPost(post: { feedbackRating: number | null }): boolean {
  return typeof post.feedbackRating === 'number'
}

export interface DevComment {
  id: string
  authorUid: string
  authorName: string | null
  authorIsDeveloper: boolean
  body: string
  createdAt: Timestamp | null
  editedAt: Timestamp | null
  upCount: number
  downCount: number
  score: number
}

// ─── Pure helpers (unit-tested) ────────────────────────────────

/// Author fields to stamp onto a new post/comment, derived from the
/// signed-in Firebase user. Anonymous users get `authorName: null`
/// (the UI renders a "Guest XXXX" handle from `authorUid`) and
/// `authorIsDeveloper: false`. A signed-in user's name is their
/// displayName, or null when they have none: the board is public, so
/// the email address is never used as a name, and a displayName that
/// is itself an address is dropped too (the security rule rejects any
/// name containing "@"). `authorIsDeveloper` is true only for the
/// developer email AND only when verified — the security rule enforces
/// the same condition, so lying here just gets the write rejected.
export function deriveAuthorFields(user: User): {
  authorUid: string
  authorName: string | null
  authorIsDeveloper: boolean
} {
  if (user.isAnonymous) {
    return { authorUid: user.uid, authorName: null, authorIsDeveloper: false }
  }
  const displayName = user.displayName?.trim() ?? ''
  const name = displayName.length > 0 && !displayName.includes('@') ? displayName : null
  const authorIsDeveloper = user.email === DEV_EMAIL && user.emailVerified === true
  return { authorUid: user.uid, authorName: name, authorIsDeveloper }
}

/// Display name for a post/comment author. Uses the stored
/// `authorName` when present; otherwise a stable short handle from
/// the last 4 chars of the UID (matches the leaderboard guest
/// convention).
export function displayNameFor(author: { authorName: string | null; authorUid: string }): string {
  if (author.authorName && author.authorName.trim().length > 0) return author.authorName.trim()
  return 'Guest ' + author.authorUid.slice(-4).toUpperCase()
}

/// The (up, down, score) deltas to apply to a target's aggregates
/// when a viewer's vote changes from `before` to `after` (null = no
/// vote). Used for optimistic UI; the authoritative values come back
/// from the Cloud Function via the snapshot listener. Mirror of the
/// trigger's own logic.
export function voteAggregateDeltas(
  before: VoteValue | null,
  after: VoteValue | null,
): { up: number; down: number; score: number } {
  const contribution = (v: VoteValue | null) =>
    v === 1 ? { up: 1, down: 0, score: 1 } : v === -1 ? { up: 0, down: 1, score: -1 } : { up: 0, down: 0, score: 0 }
  const b = contribution(before)
  const a = contribution(after)
  return { up: a.up - b.up, down: a.down - b.down, score: a.score - b.score }
}

// ─── Firestore refs ────────────────────────────────────────────

const postsCol = () => collection(firestore, 'webDevPosts')
const postRef = (postId: string) => doc(firestore, 'webDevPosts', postId)
const postVoteRef = (postId: string, voterUid: string) =>
  doc(firestore, 'webDevPosts', postId, 'votes', voterUid)
const commentsCol = (postId: string) => collection(firestore, 'webDevPosts', postId, 'comments')
const commentRef = (postId: string, commentId: string) =>
  doc(firestore, 'webDevPosts', postId, 'comments', commentId)
const commentVoteRef = (postId: string, commentId: string, voterUid: string) =>
  doc(firestore, 'webDevPosts', postId, 'comments', commentId, 'votes', voterUid)

// ─── Read mappers ──────────────────────────────────────────────

// `data` here is the result of `snapshot.data({ serverTimestamps:
// 'estimate' })` — an untyped DocumentData. We coerce every field
// defensively (a future schema field or a stray write shouldn't
// crash the renderer).
function mapPost(id: string, data: Record<string, unknown>): DevPost {
  const rating = typeof data.feedbackRating === 'number' ? data.feedbackRating : null
  const levelId = typeof data.feedbackLevelId === 'string' ? data.feedbackLevelId : null
  return {
    id,
    authorUid: typeof data.authorUid === 'string' ? data.authorUid : '',
    authorName: typeof data.authorName === 'string' ? data.authorName : null,
    authorIsDeveloper: data.authorIsDeveloper === true,
    title: typeof data.title === 'string' ? data.title : '',
    body: typeof data.body === 'string' ? data.body : '',
    createdAt: (data.createdAt as Timestamp | null) ?? null,
    editedAt: (data.editedAt as Timestamp | null) ?? null,
    archivedAt: (data.archivedAt as Timestamp | null) ?? null,
    upCount: typeof data.upCount === 'number' ? data.upCount : 0,
    downCount: typeof data.downCount === 'number' ? data.downCount : 0,
    score: typeof data.score === 'number' ? data.score : 0,
    commentCount: typeof data.commentCount === 'number' ? data.commentCount : 0,
    feedbackRating: rating,
    feedbackLevelId: levelId,
  }
}

function mapComment(id: string, data: Record<string, unknown>): DevComment {
  return {
    id,
    authorUid: typeof data.authorUid === 'string' ? data.authorUid : '',
    authorName: typeof data.authorName === 'string' ? data.authorName : null,
    authorIsDeveloper: data.authorIsDeveloper === true,
    body: typeof data.body === 'string' ? data.body : '',
    createdAt: (data.createdAt as Timestamp | null) ?? null,
    editedAt: (data.editedAt as Timestamp | null) ?? null,
    upCount: typeof data.upCount === 'number' ? data.upCount : 0,
    downCount: typeof data.downCount === 'number' ? data.downCount : 0,
    score: typeof data.score === 'number' ? data.score : 0,
  }
}

// ─── Reads ─────────────────────────────────────────────────────

/// Subscribe to the post list. `limit` caps how many are returned
/// (the page bumps it for "Load more"); `sort` chooses the order.
/// Both orderings are single-field — covered by Firestore's
/// automatic indexes, so no composite index is needed in Console.
/// Returns the unsubscribe.
export function subscribePosts(args: {
  sort: SortKey
  limit: number
  onChange: (posts: DevPost[]) => void
  onError?: (err: FirestoreError) => void
}): () => void {
  const orderField = args.sort === 'top' ? 'score' : 'createdAt'
  const q = query(postsCol(), orderBy(orderField, 'desc'), limitTo(args.limit))
  return onSnapshot(
    q,
    (snap) => {
      const out: DevPost[] = []
      snap.forEach((d) => out.push(mapPost(d.id, d.data({ serverTimestamps: 'estimate' }))))
      args.onChange(out)
    },
    (err) => args.onError?.(err),
  )
}

/// Subscribe to the Tower Log's board entries: posts the `towerLogRefresh`
/// Cloud Function created (weekly recaps + milestone posts), tagged
/// `category: "towerlog"` (server-only field — not in the client create
/// rule's allowlist, so no player post can ever masquerade as one). Reads a
/// generous `limit` with an EQUALITY-ONLY filter and sorts client-side by
/// `createdAt` (newest first) rather than adding `orderBy` — an equality
/// filter plus a sort on a different field needs a composite index in
/// Console, and Tower Log is designed to need zero Firestore config changes.
/// At Tower Log's actual volume (a handful of posts a week) this is cheap.
/// Returns the unsubscribe.
export function subscribeTowerLogPosts(args: {
  limit: number
  onChange: (posts: DevPost[]) => void
  onError?: (err: FirestoreError) => void
}): () => void {
  const q = query(postsCol(), where('category', '==', 'towerlog'), limitTo(args.limit))
  return onSnapshot(
    q,
    (snap) => {
      const out: DevPost[] = []
      snap.forEach((d) => out.push(mapPost(d.id, d.data({ serverTimestamps: 'estimate' }))))
      out.sort((a, b) => (b.createdAt?.toMillis() ?? 0) - (a.createdAt?.toMillis() ?? 0))
      args.onChange(out)
    },
    (err) => args.onError?.(err),
  )
}

/// Subscribe to one post doc. Calls back with `null` if it doesn't
/// exist (e.g. it was just deleted). Returns the unsubscribe.
export function subscribePost(
  postId: string,
  onChange: (post: DevPost | null) => void,
  onError?: (err: FirestoreError) => void,
): () => void {
  return onSnapshot(
    postRef(postId),
    (snap) => {
      if (!snap.exists()) {
        onChange(null)
        return
      }
      onChange(mapPost(snap.id, snap.data({ serverTimestamps: 'estimate' })))
    },
    (err) => onError?.(err),
  )
}

/// Subscribe to a post's comments, oldest first. Returns the
/// unsubscribe.
///
/// Defensive `limit(200)` cap: at today's scale every post has
/// <20 comments so this never truncates, but it stops a single
/// viral post (hundreds of comments × every subscriber re-
/// reading on each update) from running the Firestore read bill.
/// If we ever expect threads >200 we should paginate properly.
export function subscribeComments(
  postId: string,
  onChange: (comments: DevComment[]) => void,
  onError?: (err: FirestoreError) => void,
): () => void {
  const q = query(commentsCol(postId), orderBy('createdAt', 'asc'), limitTo(200))
  return onSnapshot(
    q,
    (snap) => {
      const out: DevComment[] = []
      snap.forEach((d) => out.push(mapComment(d.id, d.data({ serverTimestamps: 'estimate' }))))
      onChange(out)
    },
    (err) => onError?.(err),
  )
}

/// One-shot read of the viewer's vote on a post (or comment, when
/// `commentId` is given). Returns 1 / -1 / null.
export async function getMyVote(args: {
  postId: string
  commentId?: string
  voterUid: string
}): Promise<VoteValue | null> {
  const ref = args.commentId
    ? commentVoteRef(args.postId, args.commentId, args.voterUid)
    : postVoteRef(args.postId, args.voterUid)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  const v = snap.data().value
  return v === 1 || v === -1 ? v : null
}

// ─── Writes ────────────────────────────────────────────────────

/// Create a post. Callers validate length in the UI (title 1..120,
/// body 1..5000); the rule enforces it too.
export async function createPost(args: { author: User; title: string; body: string }): Promise<string> {
  const a = deriveAuthorFields(args.author)
  const ref = await addDoc(postsCol(), {
    authorUid: a.authorUid,
    authorName: a.authorName,
    authorIsDeveloper: a.authorIsDeveloper,
    title: args.title.trim(),
    body: args.body.trim(),
    createdAt: serverTimestamp(),
    editedAt: null,
    upCount: 0,
    downCount: 0,
    score: 0,
    commentCount: 0,
    lastActivityAt: serverTimestamp(),
  })
  return ref.id
}

/// Reply-email notification (board-created posts only). Registers
/// the optional address the poster left at compose time; the server
/// validates it and stores it privately in `webPostContacts` — it
/// never appears on the public post. In-app feedback posts are
/// refused server-side (they already notify through the game).
export async function registerPostContact(args: { postId: string; email: string }): Promise<void> {
  const call = httpsCallable<{ postId: string; email: string }, { ok: boolean }>(
    firebaseFunctions,
    'registerPostContact',
  )
  await call(args)
}

/// Developer-only: whether a post has a reply-notification email on
/// file. Boolean only; the address itself never reaches the client.
export async function getPostContactStatus(postId: string): Promise<boolean> {
  const call = httpsCallable<{ postId: string }, { hasEmail: boolean }>(
    firebaseFunctions,
    'getPostContactStatus',
  )
  const res = await call({ postId })
  return res.data.hasEmail === true
}

/// Edit your own post (title/body). Stamps `editedAt`.
export async function updatePost(args: { postId: string; title: string; body: string }): Promise<void> {
  await updateDoc(postRef(args.postId), {
    title: args.title.trim(),
    body: args.body.trim(),
    editedAt: serverTimestamp(),
  })
}

/// Delete a post. The `onDevPostDeleted` trigger cascades the
/// comments + votes — this only removes the post doc.
export async function deletePost(postId: string): Promise<void> {
  await deleteDoc(postRef(postId))
}

/// Toggle the post's archived state. Admin-only — the security rule
/// rejects this for non-admin callers, so the UI just needs to gate
/// the button. Archiving stamps `archivedAt` with the server time;
/// un-archiving sets it back to `null`. When archived, the rule also
/// rejects new comments and new votes on the post and its comments.
export async function setPostArchived(args: { postId: string; archive: boolean }): Promise<void> {
  await updateDoc(postRef(args.postId), {
    archivedAt: args.archive ? serverTimestamp() : null,
  })
}

/// Add a comment to a post. Body 1..3000.
export async function createComment(args: { postId: string; author: User; body: string }): Promise<string> {
  const a = deriveAuthorFields(args.author)
  const ref = await addDoc(commentsCol(args.postId), {
    authorUid: a.authorUid,
    authorName: a.authorName,
    authorIsDeveloper: a.authorIsDeveloper,
    body: args.body.trim(),
    createdAt: serverTimestamp(),
    editedAt: null,
    upCount: 0,
    downCount: 0,
    score: 0,
  })
  return ref.id
}

/// Edit your own comment. Stamps `editedAt`.
export async function updateComment(args: { postId: string; commentId: string; body: string }): Promise<void> {
  await updateDoc(commentRef(args.postId, args.commentId), {
    body: args.body.trim(),
    editedAt: serverTimestamp(),
  })
}

/// Delete a comment (your own, or any comment if you're the
/// developer). The `onDevCommentWrite` trigger decrements the post's
/// `commentCount` and removes the comment's votes.
export async function deleteComment(args: { postId: string; commentId: string }): Promise<void> {
  await deleteDoc(commentRef(args.postId, args.commentId))
}

/// Set (or retract) the viewer's vote on a post or comment.
/// `value === null` retracts (deletes the vote doc). The Cloud
/// Function recomputes the target's aggregates.
export async function setVote(args: {
  postId: string
  commentId?: string
  voterUid: string
  value: VoteValue | null
}): Promise<void> {
  const ref: DocumentReference = args.commentId
    ? commentVoteRef(args.postId, args.commentId, args.voterUid)
    : postVoteRef(args.postId, args.voterUid)
  if (args.value === null) {
    await deleteDoc(ref)
  } else {
    await setDoc(ref, { value: args.value, votedAt: serverTimestamp() })
  }
}
